import React, { useState, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Button } from 'features/components/Button';
import { Header } from 'components/Header';
import { useNavigate } from 'react-router-dom';
import { useProjectInvitations } from 'features/invite-users/useProjectInvitations';
import { useAlerts } from 'hooks/ui';
import { useLanguage } from 'features/hooks/useLanguage';
import { LanguageType } from 'types/index';
import { LedidiRequestError } from 'features/entry-form-v2/data/makeRequest';
import { useOrganizationAccessRights, useRoles, useSubscription, useUserEmail } from 'hooks/store';
import { CreatableSelect } from 'components/UI/Interactables/CreatableSelect';
import { PermissionsState, usePermissions, initialPermissionsState } from './hooks/usePermissions';

import { SelectUsersStep } from './components/SelectUsersStep';
import { PermissionsEditor } from './components/PermissionEditor';
import { ConfirmStep } from './components/ConfirmStep';
import { GroupsStep } from './components/GroupsStep';
import { StepProgressIndicator } from './components/StepProgressIndicator';
import { Card, CardContent, CardFooter } from '../components/Card';
import {
	ModulesPermissions,
	ModulesPermissionsFlags,
	ModulesPermissionsSection
} from './components/ModulesPermissionsSection';
import { OrganizationPermission } from './components/GroupsStep';
import { ROUTE_MAP } from 'features/entry-form-v2/utils/routeMap';
import { Tooltip } from '../codelist/sidebar/display/Tooltip';
import { useGetProjectQuery } from '../entry-form-v2/data/useGetProjectQuery';
import { useProjectId } from '../hooks/useProjectId';

const DICTIONARY = {
	[LanguageType.English]: {
		inviteUsers: 'Invite Users',
		inviteDescription: 'Invite collaborators to your project and set their permissions',
		errorInvalidStep: 'Error: Invalid step',
		successInvitation: (count: number) => `Successfully sent invitations to ${count} user(s)`,
		failedInvitation: 'Failed to send invitations. Please try again.',
		errorForbidden: 'You do not have permission to invite users.',
		errorConflict: 'Some users are already invited or part of the project.',
		outsideOrganization: 'Outside the organisation',
		selectUsers: 'Select Users',
		setPermissions: 'Set Permissions',
		setModules: 'Set Modules',

		selectGroups: 'Select Groups',
		confirmAndInvite: 'Confirm & Invite',
		backToProject: 'Back to Project',
		sendInvitations: 'Send Invitations',
		continue: 'Continue',
		selectRole: 'Select Role',
		role: 'Role'
	},
	[LanguageType.Norwegian]: {
		inviteUsers: 'Inviter brukere',
		inviteDescription: 'Inviter samarbeidspartnere til prosjektet ditt og angi tillatelser',
		errorInvalidStep: 'Feil: Ugyldig trinn',
		successInvitation: (count: number) => `Invitasjoner ble sendt til ${count} bruker(e)`,
		failedInvitation: 'Kunne ikke sende invitasjoner. Vennligst prøv igjen.',
		errorForbidden: 'Du har ikke tillatelse til å invitere brukere.',
		errorConflict: 'Noen brukere er allerede invitert eller del av prosjektet.',
		outsideOrganization: 'Utenfor organisasjonen',
		selectUsers: 'Velg brukere',
		setPermissions: 'Angi tillatelser',
		setModules: 'Angi moduler',
		selectGroups: 'Velg grupper',
		confirmAndInvite: 'Bekreft og inviter',
		backToProject: 'Tilbake til prosjekt',
		sendInvitations: 'Send invitasjoner',
		continue: 'Fortsett',
		selectRole: 'Velg rolle',
		role: 'Rolle'
	}
};

export enum InviteSteps {
	SelectUsers = 'Select Users',
	SetPermissions = 'Set Permissions',
	SetModules = 'Set Modules',
	SelectGroups = 'Select Groups',
	Confirm = 'Confirm & Invite'
}

interface SerializedPermissions {
	[key: string]: boolean | ModulesPermissions | OrganizationPermission[] | string;
}

export const InviteUsersPage: React.FC = () => {
	const { inviteCollaboratorSearch } = useFlags();
	const navigate = useNavigate();
	const { setNotification, setError } = useAlerts();
	const projectId = useProjectId();
	const getProjectQuery = useGetProjectQuery({ projectId });
	const language = useLanguage();
	const dictionary = DICTIONARY[language];
	const { inviteUser, isMutating } = useProjectInvitations(parseInt(projectId, 10));
	const [{ data: rolesData }] = useRoles();

	const [currentStep, setCurrentStep] = useState<InviteSteps>(InviteSteps.SelectUsers);
	const [newUsers, setNewUsers] = useState<string[]>([]);
	const [confirmedUsers, setConfirmedUsers] = useState<string[]>([]);
	const [selectedGroups, setSelectedGroups] = useState<{ groupId: number; groupName: string }[]>(
		[]
	);
	const [modules, setModules] = useState<ModulesPermissions>({
		[ModulesPermissionsFlags.projectDesign]: true,
		[ModulesPermissionsFlags.collaborators]: false
	});
	const { permissions, updatePermission, resetPermissions, setPermissions } = usePermissions();

	// Whether the user has access to delegate export access
	const [
		{
			data: { accessExportDelegation }
		}
	] = useOrganizationAccessRights();

	const [subscription] = useSubscription();
	const { isOrganizationAdmin, isSuperAdmin, isAdmin } =
		subscription.data.userTypesAndRoles.ledidiEnterprise;
	const canSkipGroupStep = isOrganizationAdmin || isSuperAdmin || isAdmin;

	const currentUserEmail = useUserEmail();

	// For testing
	const excludeEmailAddresses: string[] = ['external@example.com', currentUserEmail];

	const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

	const roleSelectItems = useMemo(() => {
		if (!rolesData?.roles) return [];
		return rolesData.roles.map(role => ({
			label: role.name,
			value: role.id
		}));
	}, [rolesData?.roles]);

	const handleRoleSelect = (value: string | null | undefined) => {
		resetPermissions();

		if (!value) {
			setSelectedRoleId(null);
			return;
		}

		const role = rolesData?.roles.find(role => role.id === value);
		if (role) {
			setSelectedRoleId(role.id);

			// Create a clean permissions object with only valid flags
			const validPermissions: Partial<PermissionsState> = {};
			const tempState = { ...initialPermissionsState };

			// Only process keys that exist in PermissionsState
			Object.entries(role.permissions).forEach(([key, value]) => {
				if (key in tempState) {
					validPermissions[key as keyof PermissionsState] = Boolean(value);
				}
			});

			setPermissions(validPermissions as PermissionsState);

			// Update modules if they exist
			if (role.permissions.modules) {
				setModules(role.permissions.modules);
			}
		}
	};

	// Serialize permissions and modules as how the backend expects them.
	const serializePermissions = (): Omit<SerializedPermissions, 'projectRoleId'> & {
		projectRoleId?: string;
	} => {
		const basePermissions = {
			// AccessRights fields
			accessExport: permissions.accessExport,
			accessProjectRead: permissions.accessProjectRead,
			accessProjectWrite: permissions.accessProjectWrite,
			accessVariablesWrite: permissions.accessVariablesWrite,
			accessVariablesRead: permissions.accessVariablesRead,
			accessEntriesAllRead: permissions.accessEntriesAllRead,
			accessEntriesAllWrite: permissions.accessEntriesAllWrite,
			accessEntriesOwnonlyWrite: permissions.accessEntriesOwnonlyWrite,
			accessEntriesOwnonlyRead: permissions.accessEntriesOwnonlyRead,
			accessEntriesOrgRead: permissions.accessEntriesOrgRead,
			accessEntriesOrgWrite: permissions.accessEntriesOrgWrite,
			accessAnalysisAll: permissions.accessAnalysisAll,
			accessAnalysisOrg: permissions.accessAnalysisOrg,
			accessAnalysisOwnonly: permissions.accessAnalysisOwnonly,
			accessPersonalData: permissions.accessPersonalData,
			accessModuleCollaborators: modules[ModulesPermissionsFlags.collaborators],
			accessModuleProjectDesign: modules[ModulesPermissionsFlags.projectDesign],
			accessEntriesAllDelete: permissions.accessEntriesAllDelete,
			accessEntriesOwnonlyDelete: permissions.accessEntriesOwnonlyDelete,
			accessEntriesOrgDelete: permissions.accessEntriesOrgDelete,
			accessCodelistRead: permissions.accessCodelistRead,
			accessCodelistWrite: permissions.accessCodelistWrite,
			accessCodelistExport: permissions.accessCodelistExport,

			// Organization accesses
			projectOrganizationAccesses: selectedGroups.map(group => ({
				organizationId: group.groupId,
				readAccess: permissions.accessEntriesOrgRead,
				writeAccess: permissions.accessEntriesOrgWrite,
				deleteAccess: permissions.accessEntriesOrgDelete,
				analysisAccess: permissions.accessAnalysisOrg
			}))
		};

		// Only include projectRoleId if it's selected
		if (selectedRoleId !== null) {
			return {
				...basePermissions,
				projectRoleId: selectedRoleId
			};
		}

		return basePermissions;
	};

	const sendInvitations = async () => {
		try {
			await inviteUser({
				projectId: parseInt(projectId),
				invitedUserEmails: confirmedUsers,
				permissions: serializePermissions()
			});

			setNotification({ message: dictionary.successInvitation(confirmedUsers.length) });
			navigate(
				ROUTE_MAP.projects.byId.collaborators.createPath({
					projectId
				})
			);
		} catch (error) {
			let errorMessage = dictionary.failedInvitation; // Default message

			if (error instanceof LedidiRequestError) {
				const specificCode = error.errors?.[0]?.code;
				if (specificCode) {
					if (specificCode === '403') {
						errorMessage = dictionary.errorForbidden;
					} else if (specificCode === '409') {
						errorMessage = dictionary.errorConflict;
					}
				}
			}
			setError({ message: errorMessage });
		}
	};

	const handleContinue = () => {
		if (currentStep === InviteSteps.SelectUsers) {
			setConfirmedUsers(newUsers);
			setCurrentStep(InviteSteps.SetPermissions);
		} else if (currentStep === InviteSteps.SetPermissions) {
			setCurrentStep(InviteSteps.SetModules);
		} else if (currentStep === InviteSteps.SetModules) {
			setCurrentStep(InviteSteps.SelectGroups);
		} else if (currentStep === InviteSteps.SelectGroups) {
			setCurrentStep(InviteSteps.Confirm);
		} else if (currentStep === InviteSteps.Confirm) {
			sendInvitations();
		}
	};

	const handleBack = () => {
		if (currentStep === InviteSteps.SelectUsers) {
			navigate(
				ROUTE_MAP.projects.byId.collaborators.createPath({
					projectId
				})
			);
		} else if (currentStep === InviteSteps.SetPermissions) {
			setCurrentStep(InviteSteps.SelectUsers);
		} else if (currentStep === InviteSteps.SetModules) {
			setCurrentStep(InviteSteps.SetPermissions);
		} else if (currentStep === InviteSteps.SelectGroups) {
			setCurrentStep(InviteSteps.SetModules);
		} else if (currentStep === InviteSteps.Confirm) {
			setCurrentStep(InviteSteps.SelectGroups);
		}
	};

	const getStepName = (step: InviteSteps): string => {
		switch (step) {
			case InviteSteps.SelectUsers:
				return dictionary.selectUsers;
			case InviteSteps.SetPermissions:
				return dictionary.setPermissions;
			case InviteSteps.SetModules:
				return dictionary.setModules;
			case InviteSteps.SelectGroups:
				return dictionary.selectGroups;
			case InviteSteps.Confirm:
				return dictionary.confirmAndInvite;
		}
	};
	const renderStepContent = () => {
		switch (currentStep) {
			case InviteSteps.SelectUsers:
				return (
					<SelectUsersStep
						newUsers={newUsers}
						setNewUsers={setNewUsers}
						excludeEmailAddresses={excludeEmailAddresses}
						inviteCollaboratorSearch={inviteCollaboratorSearch}
						canInviteExternalUser={
							getProjectQuery.data?.projectActions?.canInviteExternalUser
						}
					/>
				);
			case InviteSteps.SetPermissions:
				return (
					<>
						<div className="mb-6">
							<CreatableSelect
								label={dictionary.role}
								placeholder={dictionary.selectRole}
								items={roleSelectItems}
								value={roleSelectItems.find(item => item.value === selectedRoleId)}
								onValueSelected={handleRoleSelect}
							/>
						</div>
						<PermissionsEditor
							permissions={permissions}
							onUpdatePermission={updatePermission}
							updateModules={setModules}
							hasOrganizationExportAccess={accessExportDelegation}
							isRoleControlled={selectedRoleId !== null}
						/>
					</>
				);
			case InviteSteps.SetModules:
				return (
					<ModulesPermissionsSection
						modulesPermissions={modules}
						onUpdateModules={setModules}
						isRoleControlled={selectedRoleId !== null}
					/>
				);
			case InviteSteps.SelectGroups:
				return (
					<GroupsStep
						selectedGroups={selectedGroups}
						setSelectedGroups={setSelectedGroups}
						projectId={projectId}
						canSkipGroupSelection={canSkipGroupStep}
					/>
				);
			case InviteSteps.Confirm:
				return (
					<ConfirmStep
						confirmedUsers={confirmedUsers}
						permissions={permissions}
						modules={modules}
						selectedGroups={selectedGroups.map(group => group.groupName.toString())}
					/>
				);
		}
	};

	const isContinueDisabled =
		(currentStep === InviteSteps.SelectUsers && newUsers.length === 0) ||
		(!canSkipGroupStep &&
			currentStep === InviteSteps.SelectGroups &&
			selectedGroups.length === 0);
	const button = (
		<Button
			text={
				currentStep === InviteSteps.Confirm
					? dictionary.sendInvitations
					: dictionary.continue
			}
			variant="primary"
			onClick={handleContinue}
			disabled={isContinueDisabled || isMutating}
			loading={isMutating}
			className="min-w-[120px] transition-all duration-200 hover:opacity-90"
		/>
	);

	return (
		<>
			<Header.Main />
			<div className="min-h-[calc(100vh-4rem)] w-full bg-gradient-to-b py-8 px-4 md:px-8">
				<div className="max-w-[900px] mx-auto flex flex-col w-full">
					<div className="w-full mb-8">
						<h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
							{dictionary.inviteUsers}
						</h1>
						<p className="text-sm md:text-base text-gray-600">
							{dictionary.inviteDescription}
						</p>
					</div>

					<StepProgressIndicator
						steps={Object.values(InviteSteps).map(getStepName)}
						currentStep={getStepName(currentStep)}
					/>

					<Card className="transform transition-all duration-300">
						<CardContent>{renderStepContent()}</CardContent>

						<CardFooter className="border-t border-gray-100 bg-gray-50 px-6 py-4">
							<Button
								text={
									currentStep === InviteSteps.SelectUsers
										? dictionary.backToProject
										: 'Back'
								}
								variant="secondary"
								onClick={handleBack}
								disabled={isMutating}
								className="min-w-[100px] mr-2 transition-all duration-200 hover:bg-gray-200"
							/>
							{isContinueDisabled ? (
								<Tooltip
									content={
										currentStep === InviteSteps.SelectGroups &&
										selectedGroups.length === 0
											? dictionary.selectGroups
											: dictionary.selectUsers
									}
									placement="top"
								>
									{button}
								</Tooltip>
							) : (
								button
							)}
						</CardFooter>
					</Card>
				</div>
			</div>
		</>
	);
};

export default InviteUsersPage;
